/** Regimul de înălțime, ca șir și ca două decizii.
 *
 *  „S+P+2", „S+P+2E", „Sp+P+2E+M" și „s+p+2" sunt același regim scris în patru
 *  feluri, iar tot ce vine după îl CITEȘTE ca să numere nivelurile — deci o
 *  formă scrisă înseamnă un lucru de parsat, nu patru.
 *
 *  Se compune din două decizii, fiindcă așa se și spune regimul: câte subsoluri
 *  sub cotă și cât se ridică peste ea. Stocat rămâne tot un ȘIR, nu două numere
 *  noi în dosar: șirul e contractul cu `control-plane/services/regim.py`,
 *  singurul loc care îl traduce în niveluri pentru generare, iar două câmpuri
 *  paralele ar fi însemnat două surse de adevăr pentru același lucru — și ele
 *  se despart în prima zi în care cineva scrie numai una.
 *
 *  Aici, nu în ecran: de la despărțirea în „Detalii teren" și „Tema
 *  proiectului", regimul se alege într-unul și se folosește în amândouă (pasul
 *  2 măsoară retragerea H/2 din el).
 */
import type { Cheie } from "../i18n";
import type { RegimUrbanistic } from "./dosarclient";

/** Câte subsoluri se pot alege. */
export const SUBSOLURI: [string, Cheie][] = [
  ["", "loc.subsol.fara"],
  ["S+", "loc.subsol.unul"],
  ["2S+", "loc.subsol.doua"],
  ["D+", "loc.subsol.demisol"],
];

/** Câte etaje se pot alege peste parter. Lista de dinainte era un PRODUS între
 *  subsoluri și regimuri scrise de mână și se oprea la „P+10" — cine avea
 *  nevoie de P+11 nu-l putea alege deloc, deși motorul îl construiește. */
export const ETAJE_MAX = 20;

export function imparteRegim(v: string | null): { subsol: string; etaje: number } | null {
  const t = (v || "").trim();
  if (!t) return null;
  const m = /^(2S\+|S\+|D\+)?P(?:\+(\d+)E?)?$/i.exec(t);
  if (!m) return null;
  const etaje = m[2] ? parseInt(m[2], 10) : 0;
  if (etaje > ETAJE_MAX) return null;
  return { subsol: (m[1] || "").toUpperCase(), etaje };
}

export function compuneRegim(subsol: string, etaje: number): string {
  return `${subsol}P${etaje > 0 ? `+${etaje}` : ""}`;
}

/** Regimul care IESE DIN CERTIFICAT, nu cel ales de om.
 *
 *  Certificatul se citește în două forme: numerele deja despărțite
 *  (`niveluri_supraterane` / `niveluri_subsol`) și șirul brut. Se preferă
 *  numerele — sunt neambigue — iar șirul rămâne rezerva.
 */
export function regimDinActe(r: RegimUrbanistic | undefined | null): string | null {
  if (!r) return null;
  const sus = r.niveluri_supraterane;
  if (sus != null && sus >= 1) {
    const sub = r.niveluri_subsol ?? 0;
    const pre = sub >= 2 ? "2S+" : sub === 1 ? "S+" : "";
    return compuneRegim(pre, Math.min(ETAJE_MAX, sus - 1));
  }
  return r.regim_inaltime || null;
}

/** PRESETUL DE MĂRIME: cât dă certificatul, și sub cotă, și peste ea, fiindcă
 *  terenul se cumpără pentru cât se poate construi pe el. Abia fără certificat
 *  citit intră cifrele noastre: două subsoluri, cât cere de obicei parcarea
 *  unei clădiri de raport, și zece etaje — un număr care se vede, nu unul care
 *  trece neobservat („P" ar fi arătat ca un răspuns, când de fapt e o lipsă). */
export const SUBSOL_PRESET = "2S+";
export const ETAJE_PRESET = 10;

/** Subsolul din certificat, în forma listei `SUBSOLURI`, sau null când
 *  certificatul nu spune nimic despre subsol.
 *
 *  Separat de `regimDinActe`, fiindcă acela topește „nu se știe" și „niciun
 *  subsol" în același „P+…": presetul trebuie să le deosebească — un certificat
 *  care tace lasă presetul nostru, unul care spune „fără subsol" îl ia. Peste
 *  două subsoluri lista nu are, deci „3S" se alege ca „2S" (Oana, 01.11.2026:
 *  presetul subsolului pleacă de la certificat, nu de la parcare). */
export function subsolDinActe(r: RegimUrbanistic | undefined | null): string | null {
  if (!r) return null;
  const sub = r.niveluri_subsol;
  if (sub != null) return sub >= 2 ? "2S+" : sub === 1 ? "S+" : "";
  return imparteRegim(r.regim_inaltime || null)?.subsol ?? null;
}

/** Regimul care pleacă la generare: cel ales de om, altfel presetul.
 *
 *  Aici, nu în ecranul temei: generarea pornește din pasul Fațada, iar presetul
 *  se scrie în dosar abia atunci — v. `TemaProiect`. */
export function regimEfectiv(
  ales: string | null, r: RegimUrbanistic | undefined | null,
): { regim: string; presetat: boolean } {
  const a = imparteRegim(ales);
  const dinCU = imparteRegim(regimDinActe(r));
  return {
    regim: compuneRegim(a?.subsol ?? subsolDinActe(r) ?? SUBSOL_PRESET,
                         a?.etaje ?? dinCU?.etaje ?? ETAJE_PRESET),
    presetat: !a,
  };
}
